import { ChevronNextIcon, FolderIcon, PlusIcon } from '@svgs'
import React, { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'src/components/ui'

interface IEmptySection {
  title?: string
  description?: string
  buttonText?: string
  buttonLink?: string
  icon?: ReactNode
  onClick?: () => void
  // "go somewhere" reads as an arrow, "create something" as a plus
  buttonKind?: 'create' | 'go'
}

// An empty list says what is missing and what to do: the icon in a soft
// circle, the title, one line of explanation and the way on. (The old one put
// a small title above a 126px grey icon and the explanation in bold under it.)
const EmptySection: FC<IEmptySection> = ({
  title,
  description,
  buttonText,
  buttonLink,
  icon = <FolderIcon />,
  onClick,
  buttonKind = 'create',
}) => {
  const { t } = useTranslation()
  const action = buttonText ?? t('emptySection.buttonText')
  const buttonIcon = buttonKind === 'go' ? <ChevronNextIcon /> : <PlusIcon />

  return (
    <section className="flex flex-col items-center justify-center px-4 py-10 md:py-14">
      <div className="flex flex-col items-center gap-3 text-center w-full md:max-w-[24rem]">
        <span className="flex items-center justify-center w-20 h-20 rounded-full bg-brand-soft">
          <span className="icon w-9 h-9 fill-brand" aria-hidden="true">
            {icon}
          </span>
        </span>
        <p className="m-0 text-lg font-extrabold leading-6 text-ink">
          {title ?? t('emptySection.title')}
        </p>
        <p className="m-0 text-[0.9375rem] leading-[1.375rem] text-ink-2">
          {description ?? t('emptySection.description')}
        </p>
        {buttonLink ? (
          <Button link={buttonLink} iconAfter={buttonIcon} className="mt-2">
            {action}
          </Button>
        ) : (
          onClick && (
            <Button onClick={onClick} iconAfter={buttonIcon} className="mt-2">
              {action}
            </Button>
          )
        )}
      </div>
    </section>
  )
}

export default EmptySection
